/**
 * Account credit state for JarvisGPT.
 *
 * Surfaces the user's remaining JarvisGPT credit, fetched from
 * `GET /auth/session/credit`.
 *
 * The endpoint returns the display contract:
 *   { service, unlimited, remaining, currency, available }
 *
 * The credit is exposed as a module-scoped singleton so the header can render a
 * live balance and re-fetch it whenever the project's consumed credits change.
 */

import { ref } from 'vue'
import { apiGet } from '@/services/api'

export interface AccountCredit {
  service: string
  unlimited: boolean
  remaining: number | null
  currency: string | null
  available: boolean
}

// Module-scoped singletons so every component shares one credit instance.
const credit = ref<AccountCredit | null>(null)
const loading = ref(false)
const loadError = ref(false)

async function fetchCredit(): Promise<void> {
  loading.value = true
  loadError.value = false
  try {
    credit.value = await apiGet<AccountCredit>('/auth/session/credit')
  } catch {
    // Leave `credit` as-is; surface a soft error so consumers can render
    // "Unavailable" rather than a spinner that never resolves.
    loadError.value = true
  } finally {
    loading.value = false
  }
}

export function useAccount() {
  return {
    credit,
    loading,
    loadError,
    fetchCredit,
  }
}
